import type { AiVoiceSession } from '@restaurant/api-client';
import { z } from 'zod';

const FINISH_TIMEOUT_MS = 10_000;
const MIN_COMMIT_MS = 100;

const ServerEventSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('conversation.item.input_audio_transcription.delta'),
    delta: z.string(),
  }),
  z.object({
    type: z.literal('conversation.item.input_audio_transcription.completed'),
    transcript: z.string(),
  }),
  z.object({ type: z.literal('error') }),
]);

export interface LiveTranscription {
  send(pcm: ArrayBuffer): void;
  finish(): Promise<string>;
  close(): void;
}

export function toBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));
  }
  return btoa(binary);
}

export function openLiveTranscription(
  session: AiVoiceSession,
  handlers: { onText: (text: string) => void; onFailure: () => void },
): LiveTranscription {
  const socket = new WebSocket(session.realtimeUrl, [
    'realtime',
    `openai-insecure-api-key.${session.clientSecret}`,
  ]);
  const pending: string[] = [];
  const minCommitBytes = (session.sampleRate * 2 * MIN_COMMIT_MS) / 1000;
  let sentBytes = 0;
  let text = '';
  let closed = false;
  let settle: { resolve: (text: string) => void; reject: (error: Error) => void } | null = null;

  const shut = () => {
    closed = true;
    socket.close();
  };

  const fail = () => {
    if (closed) return;
    shut();
    if (settle) settle.reject(new Error('Live transcription failed'));
    else handlers.onFailure();
  };

  const transmit = (pcm: ArrayBuffer) => {
    sentBytes += pcm.byteLength;
    const event = JSON.stringify({ type: 'input_audio_buffer.append', audio: toBase64(pcm) });
    if (socket.readyState === WebSocket.OPEN) socket.send(event);
    else pending.push(event);
  };

  socket.addEventListener('open', () => {
    pending.splice(0).forEach((event) => socket.send(event));
  });
  socket.addEventListener('error', fail);
  socket.addEventListener('close', fail);
  socket.addEventListener('message', (message: MessageEvent<unknown>) => {
    if (typeof message.data !== 'string') return;
    const parsed = ServerEventSchema.safeParse(JSON.parse(message.data));
    if (!parsed.success) return;
    const event = parsed.data;
    if (event.type === 'error') {
      fail();
    } else if (event.type === 'conversation.item.input_audio_transcription.delta') {
      text += event.delta;
      handlers.onText(text);
    } else {
      text = event.transcript;
      shut();
      settle?.resolve(text);
    }
  });

  return {
    send: (pcm) => {
      if (!closed) transmit(pcm);
    },
    finish: () =>
      new Promise<string>((resolve, reject) => {
        if (closed) {
          reject(new Error('Live transcription failed'));
          return;
        }
        if (sentBytes < minCommitBytes) {
          shut();
          resolve(text);
          return;
        }
        const timer = setTimeout(() => fail(), FINISH_TIMEOUT_MS);
        settle = {
          resolve: (final) => {
            clearTimeout(timer);
            resolve(final);
          },
          reject: (error) => {
            clearTimeout(timer);
            reject(error);
          },
        };
        const commit = JSON.stringify({ type: 'input_audio_buffer.commit' });
        if (socket.readyState === WebSocket.OPEN) socket.send(commit);
        else pending.push(commit);
      }),
    close: shut,
  };
}
